import {
  Component,
  computed,
  effect,
  inject,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { Business, DirectusFileSchema } from '../../schemas/business';
import { BusinessCategory } from '../../schemas/business_category';
import { City } from '../../schemas/cityLocale';
import { BusinessService } from '../../services/business.service';
import { UserService } from '../../services/user.service';
import { CountryService } from '../../utils/services/country.service';
import { HandleErrorsService } from '../../utils/services/handle-errors.service';
import { ModalService } from '../../utils/services/modal.service';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { ModalComponent } from '../modal/modal.component';
import { BusinessAddress } from '../../schemas/business_address';
import { Locale } from '../../schemas/locale';
import { TextSearchFilterPipe } from '../../utils/pipes/text-search-filter.pipe';
import { ImageUploaderComponent } from '../image-uploader/image-uploader.component';
import { environment } from '../../../../environments/environment';
import { BusinessContact } from '../../schemas/business_contact';

@Component({
  selector: 'qwiko-edit-business-profile',
  imports: [
    CommonModule,
    TranslocoModule,
    FormsModule,
    ModalComponent,
    TextSearchFilterPipe,
    ImageUploaderComponent,
  ],
  templateUrl: './edit-business-profile.component.html',
  styleUrl: './edit-business-profile.component.scss',
})
export class EditBusinessProfileComponent implements OnInit {
  locales = inject(CountryService);
  modalService = inject(ModalService);
  userService = inject(UserService);
  businessService = inject(BusinessService);
  env = environment;
  // business =
  handleErrorsService = inject(HandleErrorsService);
  loading = model<boolean>(false);
  citiesLoaded = signal<boolean>(false);
  categoriesLoaded = signal<boolean>(false);
  isBusinessProfile = signal<boolean>(true);
  business = this.businessService.business;
  businessAddress = this.businessService.address;
  businessPaymentAccounts = this.businessService.businessPaymentAccounts;

  businessEffect = effect(() => {
    if (this.business()) {
      this.name.set(this.business()?.name!);
      this.store_name.set(this.business()?.store_name!);
      this.logo.set(this.business()?.logo! as DirectusFileSchema);
      this.banner.set(this.business()?.banner! as DirectusFileSchema);
      this.wifiName.set(this.business()?.wifi_name!);
      this.wifiPassword.set(this.business()?.wifi_password!);
      this.selectedCategory.set(this.business()?.category!);
      this.selectedCountry.set((this.business()?.country as Locale) || null);
      this.businessService.contacts().forEach((contact: BusinessContact) => {
        if (contact?.is_phone_number) {
          const contactNumber = contact?.phone_number || '';
          if (contactNumber && this.selectedCountry()) {
            const code = this.selectedCountry()?.phone_code || '';
            const formattedNumber = contactNumber.replace(code, '');
            this.phoneNumber.set(formattedNumber);
            this.phoneNumberID.set(contact.id!);
          }
        } else {
          if (contact.email) {
            this.email.set(contact.email);
            this.emailID.set(contact.id!);
          }
        }
      });
    }

    if (this.businessAddress()) {
      this.selectedCity.set((this.businessAddress()?.city! as City) || null);
      this.citiesLoaded.set(true);
      this.description.set(this.businessAddress()?.street_address!);
    }
  });

  name = signal<string>('');
  store_name = signal<string>('');
  categories = signal<BusinessCategory[]>([]);
  countries = signal<Locale[]>([]);
  cities = signal<City[]>([]);
  referrers = signal<any[]>([]);
  wifiName = signal<string>('');
  wifiPassword = signal<string>('');

  phoneNumberID = signal<string>('');
  phoneNumber = signal<string | null>(null);
  phoneNumberFormatted = computed<string>(() => {
    const code = this.selectedCountry()?.phone_code;
    if (!this.phoneNumber()) {
      return '';
    }
    return `${code}${this.phoneNumber()}`;
  });

  phoneNumberDisplay = computed<string>(() => {
    const code = this.selectedCountry()?.phone_code;
    const starts_with = this.selectedCountry()?.starts_with;

    if (this.phoneNumberFormatted().startsWith(code!) && starts_with) {
      const index = this.phoneNumberFormatted().indexOf(starts_with!);

      if (index !== -1) {
        const sliceIndex = index + starts_with!.length;
        const remainder = this.phoneNumberFormatted().slice(sliceIndex);

        return `${starts_with}${remainder}`;
      }
    }

    return this.phoneNumberFormatted();
  });

  callString = computed<string>(() => {
    return `tel:${this.phoneNumberFormatted()}`;
  });

  emailID = signal<string>('');
  email = signal<string>('');

  logo = signal<DirectusFileSchema | null>(null);
  logoLink = computed<string>(() => {
    return this.logo() !== null
      ? `${this.env.apiUrl}/assets/${this.logo()!.id}/${this.logo()!.filename_disk}`
      : '';
  });
  banner = signal<DirectusFileSchema | null>(null);
  bannerLink = computed<string>(() => {
    return this.banner() !== null
      ? `${this.env.apiUrl}/assets/${this.banner()!.id}/${this.banner()!.filename_disk}`
      : '';
  });
  countryFilter = signal<string>('');
  cityFilter = signal<string>('');
  categoryFilter = signal<string>('');
  selectedCountry = signal<Locale | null>(null);
  selectedCity = signal<City | null>(null);
  selectedReferrer = signal<any | null>(null);
  description = signal<string | null>(null);
  selectedCategory = signal<BusinessCategory | null>(null);

  isEdit = signal<boolean>(false);
  isEditAddress = signal<boolean>(false);
  isEditContact = signal<boolean>(false);
  isEditWifi = signal<boolean>(false);

  isProfile = signal<boolean>(true);
  isPreview = signal<boolean>(false);
  isBasicProfile = signal<boolean>(false);
  isBusinessAddress = signal<boolean>(false);
  isBusinessContact = signal<boolean>(false);
  isBusinessWifi = signal<boolean>(false);

  profileTabSyncEffect = effect(() => {
    if (this.isBasicProfile()) {
      this.isBusinessAddress.set(false);
      this.isBusinessContact.set(false);
      this.isBusinessWifi.set(false);
    }
    this.resetEditStates();
  });

  addressTabSyncEffect = effect(() => {
    if (this.isBusinessAddress()) {
      this.isBasicProfile.set(false);
      this.isBusinessContact.set(false);
      this.isBusinessWifi.set(false);
    }
    this.resetEditStates();
  });

  contactTabSyncEffect = effect(() => {
    if (this.isBusinessContact()) {
      this.isBasicProfile.set(false);
      this.isBusinessAddress.set(false);
      this.isBusinessWifi.set(false);
    }
    this.resetEditStates();
  });

  wifiTabSyncEffect = effect(() => {
    if (this.isBusinessWifi()) {
      this.isBasicProfile.set(false);
      this.isBusinessAddress.set(false);
      this.isBusinessContact.set(false);
    }
    this.resetEditStates();
  });

  toggleEdit() {
    this.isEdit.set(!this.isEdit());
  }
  async toggleEditAddress() {
    this.isEditAddress.set(!this.isEditAddress());
    await this.getCities(true);
  }
  toggleEditContact() {
    this.isEditContact.set(!this.isEditContact());
  }
  toggleEditWifi() {
    this.isEditWifi.set(!this.isEditWifi());
  }

  toggleTabs() {
    this.isProfile.set(!this.isProfile());
    this.isPreview.set(!this.isPreview());
  }

  async ngOnInit() {
    await this.getCountries();
  }

  async selectCountry(country: Locale) {
    this.selectedCountry.set(country);
    this.countryFilter.set('');
    this.closeModal('select-country');
    this.loading.set(true);
    this.modalService.open('select-city');
    this.cities.set([]);
    await this.getCities();
    this.loading.set(false);
  }

  selectCity(city: City) {
    this.selectedCity.set(city);
    this.cityFilter.set('');
    this.closeModal('select-city');
  }

  selectCategory(category: BusinessCategory) {
    this.selectedCategory.set(category);
    this.categoryFilter.set('');
    this.closeModal('select-category');
  }

  async updateBusinessProfile() {
    this.loading.set(true);

    const businessData: Business = {
      id: this.business()?.id,
      name: this.name(),
      store_name: this.store_name(),
    };

    try {
      await this.businessService.updateBusiness(businessData);
      await this.updateBusinessCategory();
      this.loading.set(false);
      this.isEdit.set(false);
      // this.router.navigateByUrl('/onboarding/add-accounts');
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }

    this.loading.set(false);
  }

  async updateBusinessWifi() {
    this.loading.set(true);

    const businessData: Business = {
      id: this.business()?.id,
      wifi_name: this.wifiName(),
      wifi_password: this.wifiPassword(),
    };

    try {
      await this.businessService.updateBusiness(businessData);
      this.loading.set(false);
      this.isEditWifi.set(false);
      // this.router.navigateByUrl('/onboarding/add-accounts');
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }

    this.loading.set(false);
  }

  async updateBusinessCategory() {
    const category: BusinessCategory = {
      business_id: this.business()?.id,
      category_id: this.selectedCategory()?.id,
    };

    return this.businessService.updateBusinessCategory(category);
  }

  async updateBusinessAddress() {
    const address: BusinessAddress = {
      id: this.businessAddress()?.id,
      country: this.selectedCountry()?.id,
      state_province: this.selectedCity()?.state_id,
      city: this.selectedCity()?.id,
      street_address: this.description(),
    };

    const businessData: Business = {
      country: this.selectedCountry()?.id!,
    };

    try {
      await this.businessService.updateBusiness(businessData);
      await this.businessService.updateBusinessAddress(address);
      this.loading.set(false);
      this.isEditAddress.set(false);
      // this.router.navigateByUrl('/onboarding/add-accounts');
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }

  async updateBusinessContact() {
    this.loading.set(true);
    const address: BusinessContact = {
      id: this.phoneNumberID(),
      phone_number: this.phoneNumberFormatted(),
      phone_number_display: this.phoneNumberDisplay(),
      is_phone_number: true,
      business_id: this.businessService.business()!.id,
    };

    try {
      if (this.email()) {
        const addressEmail: BusinessContact = {
          id: this.emailID(),
          business_id: this.businessService.business()!.id,
          email: this.email(),
        };
        await this.businessService.updateBusinessContact(addressEmail);
        this.loading.set(false);
        this.isEditContact.set(false);
      }

      return this.businessService.updateBusinessContact(address);
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
      this.loading.set(false);
    }
  }

  phoneNumberChange(event?: any) {
    if (this.phoneNumber() === null || this.phoneNumber() === '0') {
      return;
    }

    // const starts_with = this.country()?.starts_with;
    let number: string = this.phoneNumber()?.split(' ').join('') || '';
    number = number.replace(/[^0-9]/g, '');
    const intNumber: number = parseInt(number);
    const numberString: string = intNumber.toString();

    if (isNaN(intNumber) || intNumber == 0) {
      this.phoneNumber.set(null);
      return;
    }
    this.phoneNumber.set(numberString);
    return;
  }

  formatPhoneNumberOnPaste(event: ClipboardEvent) {
    // console.log('paste event:', event);
    event.preventDefault();

    let pastedText = event.clipboardData?.getData('text');
    pastedText = pastedText?.split(' ')?.join('');

    this.phoneNumber.set(pastedText ?? '');
    this.phoneNumberChange();
  }

  async getCountries() {
    const countries = await this.locales.getAllCountries();
    this.countries.set(countries);
  }

  async getCities(isInit: boolean = false) {
    this.loading.set(true);
    this.citiesLoaded.set(false);
    if (!isInit) {
      this.selectedCity.set(null);
    }
    const cities = await this.locales.getCountryCitiesByCode(
      this.selectedCountry()!.code!,
    );
    this.cities.set(cities);
    this.loading.set(false);
    this.citiesLoaded.set(true);
  }

  async openCategoryModal() {
    this.modalService.open('select-category');
    await this.getCategories();
  }

  async getCategories() {
    this.loading.set(true);
    this.categoriesLoaded.set(false);
    const categories = await this.businessService.getAllCategories();

    if (categories) {
      this.categories.set(categories);
    } else {
      this.categories.set([]);
    }

    this.loading.set(false);
    this.categoriesLoaded.set(true);
  }

  back() {
    this.isBusinessAddress.set(false);
    this.isBusinessProfile.set(true);
  }

  addBusinessAddress() {
    this.isBusinessAddress.set(false);
    this.isBusinessProfile.set(false);
  }

  openModal(modal: string) {
    this.modalService.open(modal);
  }

  closeModal(modal: string) {
    this.modalService.close(modal);
  }

  resetEditStates() {
    this.isEdit.set(false);
    this.isEditAddress.set(false);
    this.isEditContact.set(false);
    this.isEditWifi.set(false);
  }

  async resetStates() {
    // this.firstName.set('');
    // this.lastName.set('');
    // this.email.set('');
    this.loading.set(true);
    await this.businessService.getUserBusinesses();
    this.loading.set(false);
    this.resetEditStates();
    this.isProfile.set(true);
    this.isPreview.set(false);
    this.isBasicProfile.set(false);
    this.isBusinessAddress.set(false);
    this.isBusinessContact.set(false);
    this.isBusinessWifi.set(false);
  }
}
